import React from 'react';
import styled from 'styled-components';

import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { Body, SectionTitle } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import CheckIcon from '@grnhse/seedling/lib/birch/icons/check';
import ShieldExclamationIcon from '@grnhse/seedling/lib/birch/icons/shield_exclamation';
import WarningIcon from '@grnhse/seedling/lib/birch/icons/warning';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

import { FraudSignal, PositiveSignal } from 'fraud/types';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('fraud.fraud_report.signals');

export const SignalListList = styled.ul`
  margin: 0;
  padding-left: ${Tokens.spacing['3']};
`;

export const SignalItem = styled.li`
  margin-bottom: ${Tokens.spacing['0.5']};
`;

type SignalType = 'positive' | 'strong' | 'weak';

const SignalIcon = ({ type }: { type: SignalType }) => {
  switch (type) {
    case 'positive': {
      return <CheckIcon color={FullPalette.GREEN_500} />;
    }
    case 'strong': {
      return <ShieldExclamationIcon color={FullPalette.POPPY_500} />;
    }
    case 'weak': {
      return <WarningIcon color={FullPalette.YELLOW_800} />;
    }
    default: {
      return null;
    }
  }
};

const SignalList = ({
  type,
  signals,
}: {
  type: SignalType;
  signals: FraudSignal[] | PositiveSignal[];
}) => {
  if (signals.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1} data-provides={`${type}-signals-section`}>
      <Grid container spacing={1} flexWrap="nowrap">
        <Grid>
          <SignalIcon type={type} />
        </Grid>
        <Grid>
          <SectionTitle>{`${t(type)} (${signals.length})`}</SectionTitle>
        </Grid>
      </Grid>
      <Grid>
        <SignalListList>
          {signals.map((signal, index) => (
            <SignalItem key={index}>
              <Body>{signal.description}</Body>
            </SignalItem>
          ))}
        </SignalListList>
      </Grid>
    </Stack>
  );
};

const FraudSignalsList = ({
  positiveSignals,
  fraudSignals,
  showFraudSignals = true,
}: {
  positiveSignals: PositiveSignal[];
  fraudSignals: FraudSignal[];
  showFraudSignals?: boolean;
}) => {
  if (positiveSignals.length === 0 && fraudSignals.length === 0) {
    return null;
  }

  const strongSignals = fraudSignals.filter(
    (signal) => signal.severity === 'strong'
  );
  const weakSignals = fraudSignals.filter(
    (signal) => signal.severity === 'weak'
  );

  return (
    <Stack spacing={2} data-provides="fraud-signals-list">
      <SignalList type="positive" signals={positiveSignals} />
      {showFraudSignals && (
        <>
          <SignalList type="strong" signals={strongSignals} />
          <SignalList type="weak" signals={weakSignals} />
        </>
      )}
    </Stack>
  );
};

export default FraudSignalsList;
